<template>
  <v-dialog v-model="visible" max-width="800px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ $t("managedInstructions.tagAssignmentDialog.title") }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form" lazy-validation>
          <div
            v-for="(row, index) in rows"
            :key="row.uid"
            class="d-flex mb-2 align-center"
          >
            <v-select
              class="flex-grow-1"
              :items="availableTags(row)"
              item-text="tag"
              item-value="_id"
              v-model="row.employeeTag"
              :label="$t('managedInstructions.tagAssignmentDialog.tag')"
              :rules="[tagRequiredRule]"
              clearable
            />

            <v-text-field
              class="ml-4"
              v-model.number="row.interval"
              type="number"
              min="1"
              :label="
                $t('managedInstructions.tagAssignmentDialog.intervalOfMonths')
              "
              :rules="[intervalRequiredRule, intervalGreaterThanZeroRule]"
            />

            <v-btn
              icon
              class="ml-2"
              @click="removeRow(index)"
              :disabled="rows.length === 1"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>

          <v-btn text small @click="addRow">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("managedInstructions.tagAssignmentDialog.addRow") }}
          </v-btn>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">
          {{ $t("general.cancel") }}
        </v-btn>
        <v-btn color="primary" :loading="saving" @click="onSave">
          {{ $t("general.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ManagedInstructionTagAssignmentDialog",
  props: {
    instructionId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    visible: false,
    saving: false,
    assignmentId: null,
    rows: [
      {
        uid: Date.now(),
        employeeTag: null,
        interval: null,
      },
    ],
  }),
  computed: {
    ...mapGetters(["allEmployeeTags"]),
    employeeTags() {
      return this.allEmployeeTags || [];
    },
    tagRequiredRule() {
      return (v) => !!v || this.$t("general.fieldRequired");
    },
    intervalRequiredRule() {
      return (v) =>
        (v !== null && v !== undefined && v !== "") ||
        this.$t("general.fieldRequired");
    },
    intervalGreaterThanZeroRule() {
      return (v) =>
        Number(v) > 0 ||
        this.$t(
          "managedInstructions.tagAssignmentDialog.intervalMustBeGreaterThanZero",
        );
    },
  },
  methods: {
    async openDialog() {
      if (!this.instructionId) {
        return;
      }

      this.visible = true;
      await this.ensureTagsLoaded();
      await this.loadExistingAssignments();
    },

    onCancel() {
      this.visible = false;
    },

    addRow() {
      this.rows.push({
        uid: Date.now() + Math.random(),
        employeeTag: null,
        interval: null,
      });
    },

    removeRow(index) {
      this.rows.splice(index, 1);
      if (this.rows.length === 0) {
        this.addRow();
      }
    },

    async ensureTagsLoaded() {
      await this.$store.dispatch("getAllEmployeeTags");
    },

    async loadExistingAssignments() {
      if (!this.instructionId) return;

      const assignment = await this.$store.dispatch(
        "getSingleManagedInstructionAssignment",
        this.instructionId,
      );

      this.assignmentId = assignment?._id || null; // <-- add this

      if (assignment?.TagAssignments?.length) {
        this.rows = assignment.TagAssignments.map((ta) => ({
          uid: ta._id || `${ta.employeeTag}_${ta.interval}_${Math.random()}`,
          employeeTag: ta.employeeTag?._id || ta.employeeTag,
          interval: ta.interval,
        }));
      } else {
        this.rows = [{ uid: Date.now(), employeeTag: null, interval: null }];
      }
    },

    availableTags(row) {
      const selectedIds = this.rows
        .filter((r) => r.employeeTag && r !== row)
        .map((r) => r.employeeTag);

      const selectedSet = new Set(selectedIds);

      return this.employeeTags.filter((tag) => {
        const id = tag._id;
        if (!id) return false;
        if (row.employeeTag === id) return true; // keep own current selection
        return !selectedSet.has(id); // hide tags used by other rows
      });
    },

    hasDuplicateTags() {
      const tags = this.rows
        .filter((r) => r.employeeTag)
        .map((r) => r.employeeTag);

      const seen = new Set();
      for (const t of tags) {
        if (seen.has(t)) return true;
        seen.add(t);
      }
      return false;
    },

    async onSave() {
      const ok = this.$refs.form.validate();
      if (!ok) return;
      if (!this.instructionId) return;

      if (this.hasDuplicateTags()) return;

      const tagAssignments = this.rows
        .filter((r) => r.employeeTag && Number(r.interval) > 0)
        .map((r) => ({
          employeeTag: r.employeeTag,
          interval: r.interval,
        }));

      this.saving = true;
      try {
        if (this.assignmentId) {
          await this.$store.dispatch("updateManagedInstructionAssignment", {
            _id: this.assignmentId,
            managedInstruction: this.instructionId,
            TagAssignments: tagAssignments,
          });
        } else {
          const created = await this.$store.dispatch(
            "createManagedInstructionAssignment",
            {
              managedInstruction: this.instructionId,
              TagAssignments: tagAssignments,
            },
          );

          this.assignmentId = created?._id || this.assignmentId;
        }

        setTimeout(() => {
          this.$emit("saved");
          this.visible = false;
        }, 500);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
